import { IconProps } from '@chakra-ui/react'
import React, { useId, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { Tooltip } from '~/components/ui/tooltip'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { hasValue } from '~/utils/common'
import { castRef } from '~/utils/refUtils'

import { Menu, MenuOptions, MenuPositioningWrapper } from './Menu'
import { MenuItem } from './MenuItem'

export type MangoButtonMenuItem<TValue> = MenuItem<TValue> & {
  buttonLabel?: string
}

type DropdownMangoButtonProps<TValue> = {
  variant: MangoButtonVariant
  tooltipLabel?: string | React.ReactNode
  leftIcon?: SVGComponent
  rightIcon?: SVGComponent
  onButtonClick?: () => void
  items: MangoButtonMenuItem<TValue>[]
  selectedIndex?: number
  onSelect?: (value: TValue, index: number) => string | void
  onMenuOpenChange?: (isOpen: boolean) => void
  menuOptions?: MenuOptions
  mangoButtonOptions?: {
    leftIconProps?: IconProps
    rightIconProps?: IconProps
  }
} & (
  | { label: string; placeholder?: never }
  | { label?: never; placeholder: string }
)

export const DropdownMangoButton = <TValue,>({
  label,
  placeholder,
  variant,
  tooltipLabel,
  leftIcon,
  rightIcon,
  onButtonClick,
  items,
  selectedIndex,
  onSelect,
  onMenuOpenChange,
  menuOptions,
  mangoButtonOptions,
}: DropdownMangoButtonProps<TValue>) => {
  const [internalIndex, setInternalIndex] = React.useState<number>(0)
  const [menuOpen, setMenuOpen] = useState(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const buttonId = useId()

  useOutsideClick({
    ref: castRef(containerRef),
    handler: () => {
      if (menuOpen) {
        setMenuOpen(false)
        onMenuOpenChange?.(false)
      }
    },
  })

  const buttonLabel = useMemo(() => {
    const index = selectedIndex ?? internalIndex
    const selectedItem = items[index]

    return selectedItem?.buttonLabel ?? label ?? placeholder
  }, [items, selectedIndex, internalIndex])

  const handleButtonClick = () => {
    setMenuOpen((prev) => !prev)
    onMenuOpenChange?.(!menuOpen)
    onButtonClick?.()
  }
  const handleSelect = (value: TValue, index: number) => {
    onSelect?.(value, index)
    setInternalIndex(index)
    setMenuOpen(false)
    onMenuOpenChange?.(false)
    buttonRef.current?.focus()
  }

  // Tab must unmount the menu before the browser moves focus on from the
  // trigger, or focus lands on a menu item that is about to disappear.
  const handleKeyboardClose = () => {
    flushSync(() => setMenuOpen(false))
    onMenuOpenChange?.(false)
    buttonRef.current?.focus()
  }

  return (
    <MenuPositioningWrapper ref={containerRef}>
      <Tooltip
        showArrow
        content={tooltipLabel}
        positioning={{ placement: 'top' }}
        disabled={!hasValue(tooltipLabel)}
      >
        <MangoButton
          ref={buttonRef}
          id={buttonId}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          variant={variant}
          leftIcon={leftIcon}
          leftIconProps={mangoButtonOptions?.leftIconProps}
          rightIcon={rightIcon}
          rightIconProps={mangoButtonOptions?.rightIconProps}
          onClick={handleButtonClick}
          iconStrokeWidth={
            (mangoButtonOptions?.leftIconProps?.strokeWidth ??
              mangoButtonOptions?.rightIconProps?.strokeWidth ??
              '2px') as string
          }
        >
          {buttonLabel}
        </MangoButton>
      </Tooltip>
      {menuOpen && items.length > 0 && (
        <Menu
          anchorRef={castRef(buttonRef)}
          items={items}
          onSelect={handleSelect}
          options={menuOptions}
          keyboard={{
            onClose: handleKeyboardClose,
            initialFocusIndex: selectedIndex ?? internalIndex,
            labelledBy: buttonRef.current?.id || buttonId,
          }}
        />
      )}
    </MenuPositioningWrapper>
  )
}
